import { Check, ChevronDown, ChevronRight, Globe, LogOut } from 'lucide-react'
import { useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar'
import { useAuthContext } from '@/features/auth/hooks/use-auth-context'
import { useLogout } from '@/features/auth/hooks/use-logout'
import { SetupProgressCollapsed } from '@/features/setup-progress/components/SetupProgressCollapsed'
import { SetupProgressWidget } from '@/features/setup-progress/components/SetupProgressWidget'
import { SuperAdminOrgSwitcher } from '@/features/super-admin/components/SuperAdminOrgSwitcher'
import { type ActiveOrganization, useCurrentOrganization } from '@/hooks/use-current-organization'
import { useCurrentUser } from '@/hooks/use-current-user'
import { useOrganizationMemberships } from '@/hooks/use-organization-memberships'
import { useIsSuperAdmin } from '@/hooks/use-permissions'
import { useSyncedSidebarNavigation } from '@/hooks/use-synced-sidebar-navigation'
import { useLanguage, useTranslation } from '@/hooks/use-translation'
import type { I18nKey } from '@/lib/i18n/types'
import { collectNavPaths, pathIsActive } from '@/lib/routing/nav-path'
import type { DynamicNavItem, NavItem } from '@/lib/routing/routing.types'
import { cn } from '@/lib/utils'
import { AppSidebarNavItem } from './AppSidebarItem'
import { AppSidebarMenu } from './AppSidebarMenu'
import { MemberOrgSwitcher } from './MemberOrgSwitcher'

type SidebarNavItem = NavItem | DynamicNavItem

const groupContainsActivePath = (items: SidebarNavItem[], pathname: string): boolean =>
  collectNavPaths(items).some((path) => pathIsActive(pathname, path))

export function AppSidebar() {
  const { state } = useSidebar()
  const t = useTranslation()
  const { pathname } = useLocation()
  const { organization, isLoading: isLoadingOrganization } = useCurrentOrganization()

  const {
    navItems: filteredNavItems,
    footerItems: filteredFooterItems,
    profileItems: filteredProfileItems,
  } = useSyncedSidebarNavigation()

  return (
    <Sidebar
      collapsible="icon"
      className="[&_[data-sidebar=menu-button]]:min-h-9 [&_[data-sidebar=menu-button]]:rounded-[9px] [&_[data-sidebar=menu-button]]:px-3 [&_[data-sidebar=menu-button]]:transition-smooth [&_[data-sidebar=menu-button]]:duration-base [&_[data-sidebar=menu-button]]:hover:bg-white/[0.12] [&_[data-sidebar=menu-button]]:hover:text-white [&_[data-active=true]]:bg-white/[0.18] [&_[data-active=true]]:font-semibold [&_[data-active=true]]:text-white [&_[data-active=true]_span]:text-white [&_[data-active=true]_svg]:text-white"
    >
      <SidebarHeader className="gap-4 px-2 pt-4 pb-2">
        <Link to="/" className="flex items-center px-1 py-0.5">
          <img
            src="/logo.png"
            alt={t('sidebar.appName')}
            className={cn(
              'object-contain [filter:brightness(0)_invert(1)]',
              state === 'collapsed' ? 'h-7 w-7' : 'h-6 w-auto',
            )}
          />
        </Link>
        {state === 'expanded' && (
          <div className="px-1">
            <SidebarOrganization
              organization={organization}
              isLoadingOrganization={isLoadingOrganization}
            />
          </div>
        )}
      </SidebarHeader>

      <SidebarContent className="gap-0 px-2 py-2">
        {filteredNavItems.map((group) => {
          if (!('navItems' in group)) {
            return null
          }

          return (
            <SidebarNavGroup
              key={group.labelKey}
              labelKey={group.labelKey}
              navItems={group.navItems}
              isActive={groupContainsActivePath(group.navItems, pathname)}
            />
          )
        })}
      </SidebarContent>

      {state === 'expanded' ? <SetupProgressWidget /> : <SetupProgressCollapsed />}

      <SidebarFooter className="border-t px-2 py-3">
        <AppSidebarMenu navItems={filteredFooterItems}>
          <SidebarLanguageSwitcher />
          <ProfileDropdownMenu profileItems={filteredProfileItems} />
        </AppSidebarMenu>
      </SidebarFooter>
    </Sidebar>
  )
}

const SWITCHABLE_MEMBERSHIP_COUNT = 2

interface SidebarOrganizationProps {
  organization: ActiveOrganization | null
  isLoadingOrganization: boolean
}

function SidebarOrganization({ organization, isLoadingOrganization }: SidebarOrganizationProps) {
  const isSuperAdmin = useIsSuperAdmin()
  const { user, isLoading: isLoadingUser } = useAuthContext()
  const { memberships } = useOrganizationMemberships({
    enabled: !isLoadingUser && user !== null && !isSuperAdmin,
  })

  if (isSuperAdmin) return <SuperAdminOrgSwitcher currentOrganization={organization} />
  if (isLoadingOrganization) return null
  if (memberships.length >= SWITCHABLE_MEMBERSHIP_COUNT) {
    return <MemberOrgSwitcher currentOrganization={organization} memberships={memberships} />
  }
  if (!organization) return null
  return (
    <p className="truncate text-sm font-medium text-white/[0.7] transition-smooth">
      {organization.name}
    </p>
  )
}

interface SidebarNavGroupProps {
  labelKey: I18nKey
  navItems: SidebarNavItem[]
  isActive: boolean
}

function SidebarNavGroup({ labelKey, navItems, isActive }: SidebarNavGroupProps) {
  const t = useTranslation()
  const { state } = useSidebar()
  const isCollapsed = state === 'collapsed'
  const [open, setOpen] = useState(false)
  const prevActiveRef = useRef(false)

  if (prevActiveRef.current !== isActive) {
    prevActiveRef.current = isActive
    if (isActive) {
      setOpen(true)
    }
  }

  const showItems = isCollapsed || open

  return (
    <SidebarGroup className="px-0 py-0.5">
      <SidebarGroupLabel
        asChild
        className="mt-2 mb-0.5 flex h-auto w-full items-center gap-2 px-2 py-1.5 text-[11px] font-semibold uppercase tracking-[0.07em] text-white/[0.6] transition-smooth hover:text-white"
      >
        <button type="button" onClick={() => setOpen((value) => !value)} aria-expanded={open}>
          <span className="flex-1 text-left">{t(labelKey)}</span>
          <ChevronRight
            className={cn(
              'shrink-0 opacity-70 transition-transform duration-200',
              open && 'rotate-90',
            )}
          />
        </button>
      </SidebarGroupLabel>
      {showItems && (
        <SidebarGroupContent>
          <AppSidebarMenu navItems={navItems} />
        </SidebarGroupContent>
      )}
    </SidebarGroup>
  )
}

const LANGUAGES_CONFIG = [
  { code: 'en' as const, label: 'English' },
  { code: 'es' as const, label: 'Español' },
]

function LanguageFlag({ code }: { code: 'en' | 'es' }) {
  const className = 'h-[22px] w-[22px] shrink-0 rounded-md'
  if (code === 'es') {
    return (
      <svg viewBox="0 0 13 13" className={className} aria-hidden="true">
        <rect width="13" height="13" fill="#c60b1e" />
        <rect y="3.25" width="13" height="6.5" fill="#ffc400" />
      </svg>
    )
  }
  return (
    <svg viewBox="0 0 13 13" className={className} aria-hidden="true">
      <rect width="13" height="13" fill="#ffffff" />
      <rect width="13" height="1" fill="#b22234" />
      <rect y="2" width="13" height="1" fill="#b22234" />
      <rect y="4" width="13" height="1" fill="#b22234" />
      <rect y="6" width="13" height="1" fill="#b22234" />
      <rect y="8" width="13" height="1" fill="#b22234" />
      <rect y="10" width="13" height="1" fill="#b22234" />
      <rect y="12" width="13" height="1" fill="#b22234" />
      <rect width="5.2" height="7" fill="#3c3b6e" />
    </svg>
  )
}

function SidebarLanguageSwitcher() {
  const { currentLanguage, changeLanguage } = useLanguage()
  const [open, setOpen] = useState(false)
  const current = LANGUAGES_CONFIG.find((l) => l.code === currentLanguage) ?? LANGUAGES_CONFIG[0]

  return (
    <SidebarMenuItem>
      <DropdownMenu modal={false} open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton className="cursor-pointer transition-smooth">
            <div className="flex w-full items-center gap-3">
              <Globe className="h-[18px] w-[18px] text-white opacity-70" />
              <span className="flex-1 text-sm text-white">{current?.label}</span>
              <ChevronDown
                className={cn(
                  'h-4 w-4 text-white opacity-55 transition-transform duration-200',
                  open && 'rotate-180',
                )}
              />
            </div>
          </SidebarMenuButton>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align="start"
          side="top"
          sideOffset={6}
          className="w-(--radix-popper-anchor-width) overflow-hidden rounded-xl border-0 bg-white p-1.5 shadow-[0_12px_30px_rgba(8,12,40,0.32)]"
        >
          {LANGUAGES_CONFIG.map((lang) => {
            const isActive = currentLanguage === lang.code
            return (
              <DropdownMenuItem
                key={lang.code}
                onClick={() => changeLanguage(lang.code)}
                className="cursor-pointer gap-2.5 rounded-lg px-2.5 py-2 focus:bg-neutral-100"
              >
                <LanguageFlag code={lang.code} />
                <span className="flex-1 truncate text-sm font-medium text-neutral-900">
                  {lang.label}
                </span>
                {isActive && <Check className="h-4 w-4 shrink-0 text-primary" />}
              </DropdownMenuItem>
            )
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  )
}

function ProfileDropdownMenu({ profileItems }: { profileItems: NavItem[] }) {
  const t = useTranslation()
  const { currentUser } = useCurrentUser()
  const { logout } = useLogout()
  const [open, setOpen] = useState(false)

  if (!currentUser) return null

  const initial = currentUser.name.charAt(0).toUpperCase()

  return (
    <SidebarMenuItem>
      <DropdownMenu modal={false} open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton size="lg" className="cursor-pointer data-[state=open]:bg-white/[0.12]">
            <div className="flex w-full items-center gap-2.5">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white/[0.92] text-[13px] font-bold leading-none text-primary">
                {currentUser.image ? (
                  <img
                    src={currentUser.image}
                    alt={currentUser.name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  initial
                )}
              </span>

              <div className="grid min-w-0 flex-1 text-left leading-tight">
                <span className="truncate text-[13px] font-semibold text-white">
                  {currentUser.name}
                </span>
                <span className="truncate text-xs text-white/[0.6]">{currentUser.email}</span>
              </div>
              <ChevronDown
                className={cn(
                  'h-4 w-4 shrink-0 text-white opacity-55 transition-transform duration-200',
                  open && 'rotate-180',
                )}
              />
            </div>
          </SidebarMenuButton>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align="start"
          side="top"
          sideOffset={6}
          className="w-(--radix-popper-anchor-width) overflow-hidden rounded-xl border-0 bg-white p-1.5 shadow-[0_12px_30px_rgba(8,12,40,0.32)]"
        >
          <DropdownMenuLabel className="mb-1 flex items-center gap-2.5 border-b border-neutral-100 p-2 pb-2.5 font-normal">
            <span className="flex h-[30px] w-[30px] shrink-0 items-center justify-center overflow-hidden rounded-lg bg-primary-50 text-[13px] font-bold leading-none text-primary">
              {currentUser.image ? (
                <img
                  src={currentUser.image}
                  alt={currentUser.name}
                  className="h-full w-full object-cover"
                />
              ) : (
                initial
              )}
            </span>
            <span className="grid min-w-0 leading-tight">
              <span className="truncate text-[13px] font-semibold text-neutral-900">
                {currentUser.name}
              </span>
              <span className="truncate text-xs text-neutral-400">{currentUser.email}</span>
            </span>
          </DropdownMenuLabel>

          <DropdownMenuGroup>
            {profileItems.map(({ to, labelKey, icon: Icon }) => (
              <DropdownMenuItem
                asChild
                key={`${labelKey}-${to}`}
                className="cursor-pointer gap-2.5 rounded-lg px-2.5 py-2 text-neutral-900 focus:bg-neutral-100"
              >
                <AppSidebarNavItem to={to} className="flex items-center">
                  {Icon && (
                    <Icon className="h-[18px] w-[18px] shrink-0 text-neutral-700 opacity-85" />
                  )}
                  <span className="text-sm font-medium">{t(labelKey)}</span>
                </AppSidebarNavItem>
              </DropdownMenuItem>
            ))}
          </DropdownMenuGroup>

          <DropdownMenuSeparator className="my-1 bg-neutral-100" />

          <DropdownMenuItem
            variant="destructive"
            onSelect={(e) => {
              e.preventDefault()
              logout()
            }}
            className="cursor-pointer gap-2.5 rounded-lg px-2.5 py-2 font-medium"
          >
            <LogOut className="h-[18px] w-[18px] shrink-0" />
            <span className="text-sm">{t('sidebarNav.logout')}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  )
}
